import { ReactNode } from "react";
import { useOutlet } from "react-router";

import { SidePanelPortal } from "@/components/layouts/sidePanel/SidePanelPortal";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { cn } from "@/lib/cn";

export const MainViewLayout = (props: { children?: ReactNode; panelContext?: unknown }) => {
  const { children, panelContext } = props;
  const outlet = useOutlet(panelContext);

  const isPanelOpen = !!outlet;

  // The block padding is also read by what sticks to the view's edges (a list's column titles and
  // pagination), which offsets itself by it to sit flush with the edge rather than inside the padding
  return (
    <>
      <main
        className={cn(
          "flex h-full w-full flex-col gap-2 overflow-auto bg-muted px-4 pb-(--view-padding-bottom) pt-(--view-padding-top) transition-[padding-right] duration-200 ease-out [--view-padding-bottom:32px] [--view-padding-top:16px]",
          "min-[992px]:gap-4 min-[992px]:px-6 min-[992px]:[--view-padding-top:24px]",
          "min-[1200px]:gap-6 min-[1200px]:px-8 min-[1200px]:[--view-padding-top:32px]",
          isPanelOpen &&
            "min-[992px]:pr-[calc(var(--side-panel-width)+16px)] min-[1200px]:pr-[calc(var(--side-panel-width)+24px)]",
        )}
      >
        {children}
      </main>
      <SidePanelPortal isOpen={isPanelOpen}>{outlet}</SidePanelPortal>
    </>
  );
};

// --- Sub-components ---
// shrink-0: a view taller than the screen shrinks its column's items, and a header with a min-h-*
// class has given up the automatic minimum that would stop it, so its wrapped actions spill over
// what follows
MainViewLayout.Header = (props: { children?: ReactNode; className?: string }) => (
  <header className={cn("flex shrink-0 justify-between", props.className)}>{props.children}</header>
);

/**
 * The view's heading, which also names the browser tab: after the heading when it is plain text,
 * or after `documentTitle` when the heading does not say which screen this is (the dashboard's is
 * the company name).
 */
const ViewTitle = (props: { children?: ReactNode; documentTitle?: string }) => {
  const { children, documentTitle } = props;
  useDocumentTitle(documentTitle ?? (typeof children === "string" ? children : undefined));

  return <h1 className="m-0 text-2xl font-medium leading-[1.17] text-foreground">{children}</h1>;
};
MainViewLayout.ViewTitle = ViewTitle;
